import { FC, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper';
import { Box, ButtonBase, Typography, SxProps } from '@mui/material';
import { Showable, clsx, useStyles } from '@sweatpals/react-ui-kit';
import { eventCardSliderStyles } from './card-slider.styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import SeeMoreButton from '../see-more-button/see-more-button.component';
import SearchReturnedNoResults from '@/modules/_core/components/cards/Search-returned-no-results';

import classes from '@/modules/_core/components/index.module.css';
import 'swiper/css';
import 'swiper/css/navigation';

export interface CardSliderProps {
	isLoading?: boolean;
	list: unknown[];
	title?: string;
	sx?: {
		root?: SxProps;
		header?: SxProps;
		title?: SxProps;
		swipeBtn?: SxProps;
		seeMoreBtn?: SxProps;
		seeMoreBox?: SxProps;
	};
	showPagination?: boolean;
	showSkeletonOnLoading?: boolean;
	horizPadding?: number;
	keyMap?(data: unknown): string;
	component(data: unknown, isLoading: boolean): JSX.Element;
	onSeeMoreClick?(): void;
}

const CardSlider: FC<CardSliderProps> = ({
	isLoading,
	list,
	title,
	keyMap,
	component,
	sx,
	horizPadding = 30,
	onSeeMoreClick,
	showPagination = true,
	showSkeletonOnLoading = false,
}) => {
	const styles = useStyles(eventCardSliderStyles);

	const [isNavigationReady, setIsNavigationReady] = useState(false);

	const prevRef = useRef<HTMLButtonElement>(null);
	const nextRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (!isNavigationReady && prevRef.current && nextRef.current) {
			setIsNavigationReady(true);
		}
	}, [isNavigationReady, prevRef, nextRef]);

	const somethingFound = !isLoading && list?.length;
	const nothingFound = !isLoading && !list?.length;
	const showLoadingState = isLoading && !showSkeletonOnLoading;
	const showMoreButton = !!onSeeMoreClick && list?.length && !isLoading;

	return (
		<Box sx={clsx(styles.root, sx?.root)}>
			<Box sx={clsx(styles.flexRow, styles.itemsCenter, styles.eventsSliderBox, sx?.header)}>
				<Typography sx={clsx(styles.eventsSliderTitle, sx?.title)}>{title}</Typography>
				<Box
					sx={clsx(styles.sliderControls, {
						display: showPagination ? 'flex' : 'none',
					})}
				>
					<ButtonBase disableRipple ref={prevRef} title='Swipe back' sx={clsx(styles.sliderSwipeButton, sx?.swipeBtn)}>
						<ArrowBackIosIcon fontSize='inherit' sx={{ ...styles.sliderSwipeIcon, left: '4px' }} />
					</ButtonBase>

					<ButtonBase
						disableRipple
						ref={nextRef}
						title='Swipe forward'
						sx={clsx(styles.sliderSwipeButton, sx?.swipeBtn)}
					>
						<ArrowForwardIosIcon fontSize='inherit' sx={{ ...styles.sliderSwipeIcon, left: '1px' }} />
					</ButtonBase>
				</Box>
			</Box>

			{/* Swiper should initialize after refs are ready */}
			<Showable show={isNavigationReady}>
				{() => (
					<Swiper
						style={{
							...styles.swiper,
							paddingLeft: nothingFound ? '20px' : `${horizPadding}px`,
							paddingRight: nothingFound ? `20px` : undefined,
						}}
						modules={[Navigation, Pagination, Scrollbar, A11y]}
						slidesPerView='auto'
						navigation={{
							prevEl: prevRef.current,
							nextEl: nextRef.current,
							disabledClass: classes.disabledButton,
						}}
					>
						<Showable show={showLoadingState}>
							<Box display='flex' justifyContent='center'>
								<Typography sx={styles.noEventsText}>Loading...</Typography>
							</Box>
						</Showable>

						<Showable show={somethingFound}>
							{list?.map(data => (
								<SwiperSlide key={keyMap?.(data) || `${(data as { id: string }).id}`}>
									{component(data, isLoading)}
								</SwiperSlide>
							))}
						</Showable>

						<Showable show={nothingFound}>
							<Box display='flex' justifyContent='center' textAlign='center'>
								<SearchReturnedNoResults />
							</Box>
						</Showable>

						{isLoading &&
							showSkeletonOnLoading &&
							Array(4)
								.fill(null)
								.map((_, index) => <SwiperSlide key={`skeleton_${index}`}>{component(_, true)}</SwiperSlide>)}
					</Swiper>
				)}
			</Showable>

			<Showable show={showMoreButton}>
				<SeeMoreButton
					onClick={onSeeMoreClick}
					sx={{ box: clsx(styles.showMoreBox, sx?.seeMoreBox), button: sx?.seeMoreBtn }}
				/>
			</Showable>
		</Box>
	);
};

export default CardSlider;
